import { useCallback, useMemo } from 'react';
import type { AdminOrganizationListItem } from '@immo/api-contract';
import { useListParams } from '@/lib/list-view/use-list-params';

export const ORGANIZATION_PARAM = 'organization';

export type OrganizationFilter = {
  /** The chosen Standort, or `null` — also `null` without a valid workspace, or when the id is not one of `options`. */
  readonly organizationId: string | null;
  /** The chosen workspace's Standorte, by name; `[]` without a workspace. */
  readonly options: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly setOrganization: (organizationId: string | null) => void;
};

export function useOrganizationFilter(
  workspaceId: string | null,
  organizations: ReadonlyArray<Pick<AdminOrganizationListItem, 'id' | 'name' | 'workspaceId'>>,
): OrganizationFilter {
  const { params, setParams } = useListParams();

  const options = useMemo(
    () =>
      workspaceId === null
        ? []
        : organizations
            .filter((org) => org.workspaceId === workspaceId)
            .map(({ id, name }) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [workspaceId, organizations],
  );

  // Only a Standort of the chosen workspace filters: a stale or hand-edited id would
  // otherwise narrow the list to nothing with no select value on screen to clear it.
  const requested = params.get(ORGANIZATION_PARAM);
  const organizationId = options.some((option) => option.id === requested) ? requested : null;

  const setOrganization = useCallback((next: string | null) => setParams({ [ORGANIZATION_PARAM]: next }), [setParams]);

  return useMemo(() => ({ organizationId, options, setOrganization }), [organizationId, options, setOrganization]);
}
